import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@mui/material';
import styled from 'styled-components';
import { Row } from 'antd';
import { Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import { BirdLogo } from 'utils/assets/images';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import * as Sentry from "@sentry/react";
import AudioRecorder from 'containers/chat/AudioRecorder';
import SentryWrap from 'components/Sentry';
import { clearFeedbackReportResult } from 'store/feedback/actions';

const FeedBackWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: transparent;
`;

const BirdLogoImg = styled.div`
    width: 270px;
    height: 180px;
    margin-top: 20px;
    background-image: url(${BirdLogo});
    background-position: top;
    background-size: 150% 130%;
    background-repeat: no-repeat;
`;

const CloseBtn = styled.div`
    background-color: rgb(204,80,71);
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    position: absolute;
    top: 10px;
    right: 10px;
    color: white;
    z-index: 1;
`;

const FeedbackTitle = styled.div`
    color: white;
    font-size: 12px;
    font-weight: 300;
    margin-bottom: 5px;
    text-align: center;
    text-transform: uppercase;
    white-space: pre-line;
`;

const Input = styled.textarea`
    background-color: transparent;
    border: 1px solid #fff;
    color: #fff;
    box-shadow: none;
    font-size: inherit;
    font-weight: 600;
    outline: none;
    padding: 0px 2px;
    transition: all 300ms ease;
    width:100%;
    height:100px;
    text-align:left;
`;

const EmailInput = styled.input`
    background-color: transparent;
    border: 1px solid #fff;
    color: #fff;
    box-shadow: none;
    font-size: inherit;
    font-weight: 600;
    outline: none;
    padding: 8px 12px;
    transition: all 300ms ease;
    width: 100%;
    height: 40px;
    text-align: left;
    
    &::placeholder {
        color: rgba(255, 255, 255, 0.6);
    }
    
    &:invalid {
        border-color: rgba(255, 0, 0, 0.7);
    }
`;

const FeedbackSendBtn = styled.button`
    background-image: linear-gradient(to right, rgb(189,38,95), rgb(217,87,61));
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 160px;
    height: 40px;
    color: white;
    z-index: 1;
    padding: 5px;
`;

const FeedbackInner = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: transparent;
    border:1px solid #fff;
`;

const FeedbackContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 50%;
    max-width: 90vw;
    width: 600px;
    max-height: 90vh;
    background-color: rgb(58,58,58);
    position: relative;
    justify-content: flex-start;
    padding: 20px;
    padding-top: 0px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }
`;

const ModalRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: 100%;
    gap: 10px;
`;

const ReportIssueModal = ({ 
    open, 
    onClose, 
    studentName, 
    studentId,
    socketConnection,
    reCreateSocket,
    currentUser,
    currentClass
}) => {
    const dispatch = useDispatch();
    const reportResult = useSelector((state) => state.feedbackForm?.reportResult);
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const showAlert = useAlert();

    useEffect(() => {
        if (open) {
            dispatch(clearFeedbackReportResult());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (!reportResult) return;
        if (reportResult.success) {
            showAlert('Issue reported successfully. The student will be notified.', 'success');
            setLoading(false);
            handleClose();
        } else if (reportResult.reason === 'receiver_offline') {
            showAlert('Student is offline. Cannot send report. Please try again when the student is online.', 'error');
            setLoading(false);
            dispatch(clearFeedbackReportResult());
        }
    }, [reportResult]);

    const handleClose = () => {
        setDescription('');
        setEmail('');
        setEmailError('');
        setAudioUrl(null);
        setLoading(false);
        dispatch(clearFeedbackReportResult());
        onClose();
    };

    const handleAudioRecorded = (url) => {
        setAudioUrl(url);
    };

    const validateEmail = (emailValue) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailValue || emailValue.trim() === '') {
            setEmailError('Email is required');
            return false;
        }
        if (!emailRegex.test(emailValue)) {
            setEmailError('Please enter a valid email address');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);
        if (emailError) {
            validateEmail(emailValue);
        }
    };

    const handleSubmit = async () => {
        if (!description.trim()) {
            showAlert('Please enter a description', 'error');
            return;
        }

        if (!validateEmail(email)) {
            showAlert('Please enter a valid email address', 'error');
            return;
        }

        if (!socketConnection) {
            showAlert('Connection error. Please try again.', 'error');
            return;
        }

        setLoading(true);

        try {
            const messageData = {
                code: 'feedback_notification',
                receiverId: studentId,
                senderId: currentUser?.id,
                classId: currentClass?.id,
                teacherDescription: description,
                teacherName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username,
                email: email.trim(),
                audioUrl: audioUrl || null,
                type: 'teacher_report_issue',
                timestamp: Date.now()
            };

            if (socketConnection.readyState === 3) {
                reCreateSocket();
                setTimeout(() => {
                    socketConnection.send(JSON.stringify(messageData));
                }, 2000);
            } else {
                socketConnection.send(JSON.stringify(messageData));
            }
        } catch (error) {
            Sentry.captureException(`Error sending report issue: ${error}`);
            showAlert('Error sending report. Please try again.', 'error');
            setLoading(false);
        }
    };

    return (
        <SentryWrap>
        <Modal
            open={open}
            onClose={handleClose}
            style={{ height: '100%', width: '100%', backgroundColor: "transparent" }}
        >
            <FeedBackWrapper>
                <FeedbackContainer>
                    <Row style={{ justifyContent: "center" }}>
                        <BirdLogoImg />
                        <CloseBtn onClick={handleClose}>
                            <Close style={{ color: "#fff", fontWeight: "bolder", fontSize: 35 }} />
                        </CloseBtn>
                    </Row>
                    <Row style={{ paddingLeft: "20px", paddingRight: "20px" }}>
                        <FeedbackTitle>Report an issue for {studentName}</FeedbackTitle>
                    </Row>
                    <Row style={{ justifyContent: "center", paddingLeft: "20px", paddingRight: "20px" }}>
                        <Input
                            placeholder="Describe the issue..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            autoFocus
                        />
                    </Row>
                    <Row style={{ justifyContent: "center", paddingLeft: "20px", paddingRight: "20px", paddingTop: "10px" }}>
                        <div style={{ width: "100%" }}>
                            <EmailInput
                                type="email"
                                placeholder="Email * (Required)"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={() => validateEmail(email)}
                                disabled={loading}
                                required
                            />
                            {emailError && (
                                <div style={{ color: 'rgba(255, 0, 0, 0.8)', fontSize: '12px', marginTop: '4px', paddingLeft: '2px' }}>
                                    {emailError}
                                </div>
                            )}
                        </div>
                    </Row>
                    <Row style={{ paddingLeft: "20px", paddingRight: "20px", paddingTop: "10px", justifyContent: "flex-start", alignItems: "center", gap: "10px" }}>
                        <div style={{ position: 'relative' }}>
                            <AudioRecorder 
                                onRecorderDone={handleAudioRecorded}
                                bottom="60px"
                                storageLoc="/feedback/audio"
                                is_report_issue={true}
                            />
                        </div>
                        {audioUrl && (
                            <div style={{ flex: 1, maxWidth: '400px' }}>
                                <audio 
                                    controls 
                                    src={audioUrl} 
                                    style={{ width: '100%', height: '32px' }}
                                />
                            </div>
                        )}
                    </Row>
                    <Row style={{ paddingLeft: "20px", paddingRight: "20px", paddingTop: "15px" }}>
                        <ModalRow>
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                style={{ 
                                    color: '#fff', 
                                    borderColor: '#fff',
                                    minWidth: '120px'
                                }}
                            >
                                Cancel
                            </Button>
                            <FeedbackSendBtn 
                                onClick={handleSubmit} 
                                disabled={!description.trim() || !email || emailError || loading}
                            >
                                <FeedbackInner>
                                    {loading ? "Sending..." : "Report"}
                                </FeedbackInner>
                            </FeedbackSendBtn>
                        </ModalRow>
                    </Row>
                </FeedbackContainer>
            </FeedBackWrapper>
        </Modal>
        </SentryWrap>
    );
};

export default ReportIssueModal;

